import { useState, useEffect } from 'react';
import './ClientLogin.css';
import PasswordInput from './PasswordInput';
import logoDark from "../assets/opsmaticz_logo.png";

const API = '/api';

const Icon = ({ d, size = 18, strokeWidth = 2, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    email: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'm22 6-10 7L2 6'],
    lock: ['M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z', 'M7 11V7a5 5 0 0 1 10 0v4'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    arrow: ['M5 12h14', 'm12 5 7 7-7 7'],
    check: 'M20 6L9 17l-5-5',
    back: ['M19 12H5', 'M12 19l-7-7 7-7'],
    alert: ['M12 8v4', 'M12 16h.01', 'M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z'],
    shield: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
    zap: ['M13 2L3 14h9l-1 8 10-12h-9l1-8z'],
    truck: ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z', 'M18.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z'],
    mapPin: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z', 'M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z'],
    fileText: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8']
};

export default function ClientLogin({ onLogin }) {
    const [view, setView] = useState('login'); // 'login' | 'forgot' | 'forgot-sent'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [forgotEmail, setForgotEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [branding, setBranding] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '');
        const opId = params.get('op');
        if (opId) {
            fetch(`${API}/operators/${opId}`)
                .then(res => res.json())
                .then(data => { if (data && data.companyName) setBranding(data); })
                .catch(err => console.error("Error fetching branding:", err));
        }
    }, []);

    const handleEmailBlur = async () => {
        if (!email || !email.includes('@') || branding) return;
        try {
            const res = await fetch(`${API}/client-branding?email=${encodeURIComponent(email)}`);
            if (res.ok) {
                const data = await res.json();
                if (data && data.companyName) setBranding(data);
            }
        } catch (err) { /* silently fail */ }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API}/client-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Login failed');
            localStorage.setItem('ops-user', JSON.stringify(data));
            onLogin(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API}/client-forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: forgotEmail }),
            });
            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                throw new Error('Server is unavailable. Please try again in a moment.');
            }
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to send reset email. Please try again.');
            setView('forgot-sent');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            {/* ── Left Hero Visual Side ── */}
            <div className="login-side-decoration client-theme-glow">
                <div className="hero-blueprint-bg"></div>
                <div className="ambient-orbs">
                    <div className="orb orb-1"></div>
                    <div className="orb orb-2"></div>
                </div>

                <div className="login-brand-header">
                    <div className="brand-logo-wrap">
                        {branding?.companyLogo && (
                            <img
                                src={`${API}/uploads/${branding.companyLogo}`}
                                alt="Company Logo"
                                className="brand-logo-img"
                                style={{ borderRadius: '8px' }}
                            />
                        )}
                    </div>
                    <div className="portal-badge-pill">
                        <span className="portal-badge-dot"></span>
                        {branding?.companyName ? `${branding.companyName} Portal` : 'Client Portal'}
                    </div>
                </div>

                <div className="login-hero-card">
                    <div className="hero-status-pill">
                        <span className="pulse-green"></span>
                        Corporate Portal Operational
                    </div>
                    <h2>Passenger & Trip Management</h2>
                    <p>Schedule trips, manage invoices, monitor passenger transport in real-time, and access detailed corporate travel analytics.</p>
                    <div className="hero-feature-tags">
                        <span className="hero-tag">
                            <Icon d={icons.user} size={14} /> Passenger Rosters
                        </span>
                        <span className="hero-tag">
                            <Icon d={icons.mapPin} size={14} /> Live Trip Tracking
                        </span>
                        <span className="hero-tag">
                            <Icon d={icons.fileText} size={14} /> Automated Invoices
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Right Side Form ── */}
            <div className="login-main">
                <div className="login-box">
                    <div className="mobile-brand-bar">
                        <img src={logoDark} alt="OpsMaticz Logo" />
                    </div>

                    {view === 'login' && (
                        <>
                            <div className="login-box-header">
                                <div className="portal-title-icon">
                                    <Icon d={icons.user} size={24} />
                                </div>
                                <h1>Welcome Back</h1>
                                <p>{branding ? `Sign in to your ${branding.companyName} Client Panel` : 'Sign in to your Client Control Panel'}</p>
                            </div>

                            <form className="login-form-premium" onSubmit={handleSubmit}>
                                {error && (
                                    <div className="error-message-shake">
                                        <Icon d={icons.alert} size={18} />
                                        <span>{error}</span>
                                    </div>
                                )}

                                <div className="input-field">
                                    <label>Email Address</label>
                                    <div className="input-inner">
                                        <Icon d={icons.email} size={18} className="input-icon-svg" />
                                        <input
                                            type="email"
                                            placeholder="client@company.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            onBlur={handleEmailBlur}
                                            required
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                <div className="input-field">
                                    <div className="label-row">
                                        <label>Password</label>
                                        <button 
                                            type="button" 
                                            onClick={() => { setView('forgot'); setError(''); setForgotEmail(email); }}
                                            className="forgot-link-btn"
                                        >
                                            Forgot Password?
                                        </button>
                                    </div>
                                    <div className="input-inner">
                                        <Icon d={icons.lock} size={18} className="input-icon-svg" />
                                        <PasswordInput
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>

                                <button type="submit" className="submit-btn" disabled={loading}>
                                    {loading ? (
                                        <span className="loader-text">
                                            <span className="dot-flashing"></span>
                                            Authenticating...
                                        </span>
                                    ) : (
                                        <>
                                            <span>Sign In</span>
                                            <Icon d={icons.arrow} size={18} />
                                        </>
                                    )}
                                </button>
                            </form>
                        </>
                    )}

                    {view === 'forgot' && (
                        <>
                            <div className="login-box-header">
                                <h1>Reset Password</h1>
                                <p>Enter your registered email address to receive a recovery link</p>
                            </div>

                            <form className="login-form-premium" onSubmit={handleForgotSubmit}>
                                {error && (
                                    <div className="error-message-shake">
                                        <Icon d={icons.alert} size={18} />
                                        <span>{error}</span>
                                    </div>
                                )}

                                <div className="input-field">
                                    <label>Email Address</label>
                                    <div className="input-inner">
                                        <Icon d={icons.email} size={18} className="input-icon-svg" />
                                        <input
                                            type="email"
                                            placeholder="your@company.com"
                                            value={forgotEmail}
                                            onChange={(e) => setForgotEmail(e.target.value)}
                                            required
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                <button type="submit" className="submit-btn" disabled={loading}>
                                    {loading ? (
                                        <span className="loader-text">
                                            <span className="dot-flashing"></span>
                                            Sending Link...
                                        </span>
                                    ) : (
                                        <>
                                            <span>Send Reset Link</span>
                                            <Icon d={icons.arrow} size={18} />
                                        </>
                                    )}
                                </button>
                            </form>

                            <div style={{ marginTop: 24, textAlign: 'center' }}>
                                <button 
                                    onClick={() => { setView('login'); setError(''); }}
                                    className="forgot-link-btn"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                                >
                                    <Icon d={icons.back} size={15} /> Back to Sign In
                                </button>
                            </div>
                        </>
                    )}

                    {view === 'forgot-sent' && (
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                            <div style={{
                                width: 56, height: 56, borderRadius: '50%',
                                background: 'rgba(0, 178, 169, 0.1)',
                                border: '1px solid rgba(0, 178, 169, 0.3)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 20px',
                                color: '#00B2A9'
                            }}>
                                <Icon d={icons.check} size={28} />
                            </div>
                            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>Check Your Email</h2>
                            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, margin: '0 0 6px' }}>
                                We've sent a password reset link to
                            </p>
                            <p style={{ fontSize: 15, fontWeight: 700, color: '#00B2A9', margin: '0 0 20px' }}>{forgotEmail}</p>
                            <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6, margin: '0 0 24px' }}>
                                Click the link in the email to reset your password. The link will expire in <strong>1 hour</strong>.
                            </p>
                            <button
                                onClick={() => { setView('login'); setError(''); setForgotEmail(''); }}
                                className="submit-btn"
                            >
                                Back to Login
                            </button>
                        </div>
                    )}

                    {/* ── Switcher Navigation Footer ── */}
                    <div className="portal-switcher-box">
                        <div className="portal-switcher-label">Switch Portal Access</div>
                        <div className="portal-tabs">
                            <a href="#superadmin-login" className="portal-tab-btn">
                                <Icon d={icons.shield} size={15} />
                                <span>Admin</span>
                            </a>
                            <a href="#operator-login" className="portal-tab-btn">
                                <Icon d={icons.zap} size={15} />
                                <span>Operator</span>
                            </a>
                            <a href="#vendor-login" className="portal-tab-btn">
                                <Icon d={icons.truck} size={15} />
                                <span>Vendor</span>
                            </a>
                            <a href="#client-login" className="portal-tab-btn active">
                                <Icon d={icons.user} size={15} />
                                <span>Client</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
